import lunr, { Token } from "lunr";
import {
  charPrefixOrSuffixExceptions,
  charWithinStrExceptions,
  wordToNumber,
} from "../../utils/searchPrototypeData";
import { HyphenatedToken, MiscSearchData } from "../../types";
import clone from "lodash/clone";

export function lunrNumberPlugIn(builder: lunr.Builder) {
  const pipelineFunction = function (currToken: Token) {
    const currTokenStr = currToken.toString();
    const isNumber = Object.hasOwn(wordToNumber, currTokenStr);
    if (isNumber) {
      const updatedToken = currToken.update(() => wordToNumber[currTokenStr]);
      return updatedToken;
    }
    return currToken;
  };
  lunr.Pipeline.registerFunction(pipelineFunction, "handleNumbers");
  builder.pipeline.after(lunr.stemmer, pipelineFunction);
  builder.searchPipeline.after(lunr.stemmer, pipelineFunction);
}

export function lunrCustomTrimmerPlugIn(builder: lunr.Builder) {
  lunr.trimmer = function (token: Token) {
    return token.update(function (s) {
      const hasPrefixOrSuffixException = Object.hasOwn(
        charPrefixOrSuffixExceptions,
        s
      );
      if (hasPrefixOrSuffixException) {
        return s;
      }

      const trimmedStr = s
        .replace(/^\W+/, "")
        .replace(/\W+$/, "")
        .replace(/('s)$/, "");

      const hasWithinStrException = Object.hasOwn(
        charWithinStrExceptions,
        trimmedStr
      );
      if (hasWithinStrException) {
        return charWithinStrExceptions[trimmedStr];
      }

      return trimmedStr;
    });
  };

  lunr.Pipeline.registerFunction(lunr.trimmer, "customTrimmer"); // replaces the default trimmer for the index pipeline
  builder.searchPipeline.before(lunr.stemmer, lunr.trimmer); // adds the custom trimmer to the search pipeline
}

export function lunrSearch(
  query: string,
  index: lunr.Index,
  miscSearchData: MiscSearchData
): lunr.Index.Result[] {
  const { wildcardWords, hyphenatedWords, commonMathPhrases } = miscSearchData;
  return index.query(function (q) {
    const processedQuery = hyphenatedWords
      ? checkQueryForHyphen(query, hyphenatedWords)
      : query;
    const tokens = lunr.tokenizer(processedQuery);
    tokens.forEach((currToken: Token, i: number) => {
      let currTokenStr = currToken.toString();
      let required = false;
      let isWildcard = false;

      if (currTokenStr.startsWith("+") && currTokenStr.length > 1) {
        required = true;
        currTokenStr = currTokenStr.slice(1);
      } else {
        const mathPhraseStart = commonMathPhrases
          ? Object.keys(commonMathPhrases).find((key) =>
              currTokenStr.startsWith(key.toLowerCase())
            )
          : undefined;
        if (mathPhraseStart && commonMathPhrases) {
          // Search remaining tokens for the math phrase
          const restOfPhrase = commonMathPhrases[mathPhraseStart];
          const restOfPhraseTokens = tokens
            .slice(i + 1)
            .filter((token: Token) => {
              const mathPhraseEnd = restOfPhrase.find((word) =>
                token.toString().startsWith(word.toLowerCase())
              );
              return !!mathPhraseEnd;
            });
          if (restOfPhraseTokens.length) {
            restOfPhraseTokens.forEach((token: Token) => {
              token.update(() => "+" + token.toString());
            });
            required = true;
          }
        }
        if (!required) {
          const wildcard = wildcardWords?.find((key) =>
            currTokenStr.startsWith(key)
          );
          if (wildcard) {
            currTokenStr = wildcard;
            isWildcard = true;
          }
        }
      }

      const settings = {
        presence: required
          ? lunr.Query.presence.REQUIRED
          : lunr.Query.presence.OPTIONAL,
        ...(isWildcard
          ? {
              wildcard: lunr.Query.wildcard.TRAILING,
              usePipeline: false,
            }
          : {}),
      };
      q.term(currTokenStr, settings);
    });
  });
}

function checkQueryForHyphen(
  query: string,
  commonHyphenatedTokens: HyphenatedToken
) {
  const lowerCaseQuery = query.toLowerCase();
  Object.keys(commonHyphenatedTokens).forEach((key) => {
    const hyphenatedToken = commonHyphenatedTokens[key];
    if (lowerCaseQuery.includes(key)) {
      query = lowerCaseQuery.replaceAll(key, hyphenatedToken);
    }
  });
  return query;
}

export function whitelistPlugin(builder: lunr.Builder, whitelist: string[]) {
  function stopWordWhitelist(token: lunr.Token) {
    // pass any token in the whitelist through
    if (whitelist.includes(token.toString())) return token;
    // all other tokens should be passed through the normal filter
    return lunr.stopWordFilter(token);
  }

  lunr.Pipeline.registerFunction(stopWordWhitelist, "stopWordWhitelist");

  builder.pipeline.before(lunr.stopWordFilter, stopWordWhitelist);
  builder.pipeline.remove(lunr.stopWordFilter);
}

/**
 * Custom tokenizer: It uses the native lunr tokenizer, but also handles hyphens in strings by replacing them with spaces AND removing them
 * @param obj The object to tokenize
 * @param metadata Optional metadata to pass to the tokenizer
 * @returns An array of tokens
 */
function myTokenizer(
  obj?: string | object | object[] | null,
  metadata?: Record<string, unknown>
) {
  if (obj === null || obj === undefined) {
    return [];
  }

  if (Array.isArray(obj)) {
    return obj.map(function (t) {
      return new lunr.Token(
        lunr.utils.asString(t).toLowerCase(),
        metadata ? clone(metadata) : {}
      );
    });
  }

  const str = handleHyphens(obj.toString().toLowerCase());
  const len = str.length;
  const tokens = [];

  for (let sliceEnd = 0, sliceStart = 0; sliceEnd <= len; sliceEnd++) {
    const char = str.charAt(sliceEnd);
    const sliceLength = sliceEnd - sliceStart;

    if (char.match(lunr.tokenizer.separator) || sliceEnd === len) {
      if (sliceLength > 0) {
        const tokenMetadata: Record<string, unknown> = clone(metadata) || {};
        tokenMetadata["position"] = [sliceStart, sliceLength];
        tokenMetadata["index"] = tokens.length;

        tokens.push(
          new lunr.Token(str.slice(sliceStart, sliceEnd), tokenMetadata)
        );
      }
      sliceStart = sliceEnd + 1;
    }
  }

  return tokens;
}

lunr.tokenizer = Object.assign(myTokenizer, {
  separator: /[\s\-/]+/,
});

/**
 * Handles hyphens in strings by replacing them with spaces AND removing them
 * @param str The string to handle
 * @returns The string with spaces and duplicates added
 */
function handleHyphens(str: string) {
  if (!str.includes("-")) {
    return str;
  }
  const withSpace = str.replace(/-/g, " ");
  const withoutSpace = str.replace(/-/g, "");
  return `${withSpace} ${withoutSpace}`;
}

export const whitelistStopWords = ["least", "like", "likely", "by", "of"];
